import { useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PlayCircle } from "lucide-react";
import HelpVideoPlayer, { videoThumb } from "@/components/HelpVideoPlayer";

const EMPTY = {
  category_key: "", title_en: "", title_hi: "", description: "", url: "",
  thumbnail: "", screen_key: "", language: "hi", display_order: 0, enabled: true,
};

const fileToThumb = (file) =>
  new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const w = 480, h = Math.round((img.height / img.width) * 480) || 270;
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      res(c.toDataURL("image/jpeg", 0.7));
    };
    img.src = URL.createObjectURL(file);
  });

function VideoForm({ open, onOpenChange, initial, categories, screenKeys, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial); }, [initial]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const editing = !!initial.video_id;

  const save = async () => {
    if (!f.category_key || !f.title_en.trim() || !f.url.trim()) { toast.error("Category, English title and URL are required"); return; }
    setSaving(true);
    try {
      const body = { ...f, display_order: Number(f.display_order) || 0 };
      const r = editing
        ? await api.patch(`/admin/help/videos/${f.video_id}`, body)
        : await api.post("/admin/help/videos", body);
      onSaved(r.data, editing);
      onOpenChange(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="help-admin-video-form">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Video" : "Add Video"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category</label>
            <Select value={f.category_key} onValueChange={(v) => set("category_key", v)}>
              <SelectTrigger className="am-input mt-1" data-testid="help-admin-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.name_en}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input data-testid="help-admin-form-title-en" className="am-input" placeholder="Title (English)" value={f.title_en} onChange={(e) => set("title_en", e.target.value)} />
          <Input data-testid="help-admin-form-title-hi" className="am-input" placeholder="Title (हिन्दी)" value={f.title_hi} onChange={(e) => set("title_hi", e.target.value)} />
          <Input data-testid="help-admin-form-url" className="am-input" placeholder="Video URL — YouTube / Vimeo / Firebase Storage" value={f.url} onChange={(e) => set("url", e.target.value)} />
          <Textarea className="am-input" rows={2} placeholder="Description (optional)" value={f.description} onChange={(e) => set("description", e.target.value)} />
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Thumbnail (URL or upload)</label>
            <div className="flex gap-2 mt-1 items-center">
              <Input className="am-input flex-1" placeholder="https://… (auto for YouTube)" value={f.thumbnail?.startsWith("data:") ? "(uploaded image)" : f.thumbnail}
                onChange={(e) => set("thumbnail", e.target.value)} />
              <input id="help-thumb-file" type="file" accept="image/*" className="hidden"
                onChange={async (e) => { const file = e.target.files?.[0]; if (file) set("thumbnail", await fileToThumb(file)); e.target.value = ""; }} />
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => document.getElementById("help-thumb-file").click()}>Upload</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Screen button</label>
              <Select value={f.screen_key || "none"} onValueChange={(v) => set("screen_key", v === "none" ? "" : v)}>
                <SelectTrigger className="am-input mt-1" data-testid="help-admin-form-screen"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {screenKeys.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Language</label>
              <Select value={f.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="am-input mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hi">हिन्दी</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">العربية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Order</label>
              <Input type="number" className="am-input w-20 h-9" value={f.display_order} onChange={(e) => set("display_order", e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Enabled</label>
              <Switch checked={f.enabled} onCheckedChange={(v) => set("enabled", v)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="help-admin-form-save" className="am-btn-primary w-full" disabled={saving} onClick={save}>{saving ? "…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminHelpVideos() {
  const [data, setData] = useState({ categories: [], videos: [], screen_keys: [] });
  const [form, setForm] = useState(null); // video being added/edited
  const [catForm, setCatForm] = useState(null); // {name_en, name_hi} for new category
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/help/videos").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSaved = () => load();

  const quickPatch = async (video, changes) => {
    try {
      await api.patch(`/admin/help/videos/${video.video_id}`, changes);
      setData((d) => ({ ...d, videos: d.videos.map((v) => (v.video_id === video.video_id ? { ...v, ...changes } : v)) }));
    } catch { toast.error("Update failed"); }
  };

  const del = async (video) => {
    if (!window.confirm(`Delete "${video.title_en}"?`)) return;
    try {
      await api.delete(`/admin/help/videos/${video.video_id}`);
      setData((d) => ({ ...d, videos: d.videos.filter((v) => v.video_id !== video.video_id) }));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const patchCat = async (cat, changes) => {
    try {
      await api.patch(`/admin/help/categories/${cat.key}`, changes);
      setData((d) => ({ ...d, categories: d.categories.map((c) => (c.key === cat.key ? { ...c, ...changes } : c)) }));
    } catch { toast.error("Update failed"); }
  };

  const addCategory = async () => {
    if (!catForm?.name_en?.trim()) { toast.error("English name required"); return; }
    try {
      const r = await api.post("/admin/help/categories", { name_en: catForm.name_en, name_hi: catForm.name_hi || "" });
      setData((d) => ({ ...d, categories: [...d.categories, r.data] }));
      setCatForm(null);
      toast.success("Category created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  return (
    <DashboardShell
      title="Help Videos"
      subtitle="Training video library — categories, links & screen buttons"
      action={
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setCatForm({ name_en: "", name_hi: "" })} data-testid="help-admin-add-category">
            <Plus className="w-4 h-4 mr-1" /> Category
          </Button>
          <Button className="am-btn-primary rounded-full" onClick={() => setForm({ ...EMPTY })} data-testid="help-admin-add-video">
            <Plus className="w-4 h-4 mr-1" /> Add Video
          </Button>
        </div>
      }
    >
      <div className="space-y-6 max-w-5xl mx-auto">
        {data.categories.map((c) => {
          const vids = data.videos.filter((v) => v.category_key === c.key);
          return (
            <div key={c.key} className="am-card p-5" data-testid={`help-admin-cat-${c.key}`}>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="font-display font-bold flex-1">{c.name_en} <span className="text-muted-foreground font-normal text-sm">· {c.name_hi}</span></h2>
                <span className="text-xs text-muted-foreground">{vids.length} videos</span>
                <Switch checked={c.enabled} onCheckedChange={(v) => patchCat(c, { enabled: v })} data-testid={`help-admin-cat-toggle-${c.key}`} />
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...EMPTY, category_key: c.key })}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {vids.length === 0 ? (
                <div className="text-xs text-muted-foreground">No videos yet.</div>
              ) : (
                <div className="space-y-2">
                  {vids.map((v) => (
                    <div key={v.video_id} className="flex items-center gap-3 border rounded-2xl p-2" data-testid={`help-admin-video-row-${v.video_id}`}>
                      <button className="w-20 h-12 rounded-xl bg-muted overflow-hidden shrink-0 grid place-items-center" onClick={() => setPreview(v)}>
                        {videoThumb(v) ? <img src={videoThumb(v)} alt="" className="w-full h-full object-cover" /> : <PlayCircle className="w-5 h-5 text-muted-foreground" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{v.title_en}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          <span className="uppercase font-bold">{v.provider}</span>
                          {v.screen_key && <> · button: {v.screen_key}</>} · order {v.display_order} · {v.language}
                        </div>
                      </div>
                      <Switch checked={v.enabled} onCheckedChange={(en) => quickPatch(v, { enabled: en })} data-testid={`help-admin-video-toggle-${v.video_id}`} />
                      <Button size="sm" variant="ghost" onClick={() => setForm({ ...EMPTY, ...v })} data-testid={`help-admin-video-edit-${v.video_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-700" onClick={() => del(v)} data-testid={`help-admin-video-delete-${v.video_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {form && (
        <VideoForm open={!!form} onOpenChange={(o) => !o && setForm(null)} initial={form}
          categories={data.categories} screenKeys={data.screen_keys} onSaved={onSaved} />
      )}

      <Dialog open={!!catForm} onOpenChange={(o) => !o && setCatForm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">New Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="help-admin-cat-name-en" className="am-input" placeholder="Name (English)" value={catForm?.name_en || ""} onChange={(e) => setCatForm({ ...catForm, name_en: e.target.value })} />
            <Input data-testid="help-admin-cat-name-hi" className="am-input" placeholder="Name (हिन्दी)" value={catForm?.name_hi || ""} onChange={(e) => setCatForm({ ...catForm, name_hi: e.target.value })} />
          </div>
          <DialogFooter><Button data-testid="help-admin-cat-save" className="am-btn-primary w-full" onClick={addCategory}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {preview && <HelpVideoPlayer video={preview} open={!!preview} onOpenChange={(o) => !o && setPreview(null)} />}
    </DashboardShell>
  );
}
